import { useState } from 'react'

import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Pencil } from '@factorialco/f0-react/icons/app'
import {
  AtsBulkDeleteQuestionTemplatesDocument,
  AtsDeleteQuestionTemplateDocument,
  AtsQuestionTemplateFragment,
  AtsQuestionTemplatesListDocument,
} from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getQuestionTagColor, getQuestionTypeLabel } from 'modules/ats/lib/utils/customQuestion'

import { EditQuestionTemplate } from './EditQuestionTemplate'

export const QuestionTemplatesTable = () => {
  const { company } = useCurrentSession()
  const { openDialog } = useConfirmationDialog()

  const [deleteQuestionTemplate] = useMutation(AtsDeleteQuestionTemplateDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsQuestionTemplate,
    refetchQueries: [AtsQuestionTemplatesListDocument],
  })

  const [bulkDeleteQuestionTemplates] = useMutation(AtsBulkDeleteQuestionTemplatesDocument, {
    extractorFn: (data) => data.atsMutations.bulkDeleteAtsQuestionTemplate,
    refetchQueries: [AtsQuestionTemplatesListDocument],
  })

  const [editQuestionTemplate, setEditQuestionTemplate] =
    useState<AtsQuestionTemplateFragment | null>(null)

  const removeQuestionTemplate = async (questionTemplateId: number) => {
    const result = await deleteQuestionTemplate({
      variables: { id: questionTemplateId },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  const bulkRemoveQuestionTemplates = async (selectedIds: number[]) => {
    const result = await bulkDeleteQuestionTemplates({
      variables: { ids: selectedIds },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  const source = useGraphqlDataCollectionSource({
    document: AtsQuestionTemplatesListDocument,
    mapData: (data) => data.ats.questionTemplatesConnection.nodes,
    search: {
      enabled: true,
    },
    mapVariables: ({ filters, sortings, search }) => {
      const questionType = filters.questionType
      const questionTypeFilter = questionType ? { in: questionType } : undefined

      const mandatoryFilter = filters.mandatory
        ? filters.mandatory.length === 1
          ? { eq: filters.mandatory[0] }
          : undefined
        : undefined
      const autoDisqualifyFilter = filters.autoDisqualify
        ? filters.autoDisqualify.length === 1
          ? { eq: filters.autoDisqualify[0] }
          : undefined
        : undefined

      return {
        companyId: company.id,
        questionType: questionTypeFilter,
        mandatory: mandatoryFilter,
        autoDisqualify: autoDisqualifyFilter,
        sort: sortings,
        search: search,
      }
    },
    mapPagination: (data) => ({
      total: data.ats.questionTemplatesConnection.totalCount ?? 0,
    }),
    filters: {
      questionType: {
        type: 'in',
        label: i18n.t('ats.settings.templates.custom_questions.table_headers.question_type'),
        options: {
          options: [
            { label: i18n.t('ats.questions.types.text'), value: 'text' },
            { label: i18n.t('ats.questions.types.long_text'), value: 'long_text' },
            { label: i18n.t('ats.questions.types.single_choice'), value: 'single_choice' },
            { label: i18n.t('ats.questions.types.multiple_choice'), value: 'multiple_choice' },
            { label: i18n.t('ats.questions.types.file'), value: 'file' },
          ],
        },
      },
      mandatory: {
        type: 'in',
        label: i18n.t('ats.settings.templates.custom_questions.table_headers.mandatory'),
        options: {
          options: [
            { label: capitalize(i18n.t('common.yes')), value: true },
            { label: capitalize(i18n.t('common.no')), value: false },
          ],
        },
      },
      autoDisqualify: {
        type: 'in',
        label: i18n.t('ats.settings.templates.custom_questions.table_headers.disqualifies'),
        options: {
          options: [
            { label: capitalize(i18n.t('common.yes')), value: true },
            { label: capitalize(i18n.t('common.no')), value: false },
          ],
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.questions.types.text'),
        filter: {
          questionType: ['text'],
        },
      },
      {
        label: i18n.t('ats.questions.types.long_text'),
        filter: {
          questionType: ['long_text'],
        },
      },
      {
        label: i18n.t('ats.questions.types.single_choice'),
        filter: {
          questionType: ['single_choice'],
        },
      },
      {
        label: i18n.t('ats.questions.types.multiple_choice'),
        filter: {
          questionType: ['multiple_choice'],
        },
      },
      {
        label: i18n.t('ats.questions.types.file'),
        filter: {
          questionType: ['file'],
        },
      },
    ],
    selectable: (item) => item.id,
    bulkActions: (action) => {
      return {
        primary: [
          {
            label: action.allSelected
              ? i18n.t('ats.settings.templates.custom_questions.selection.delete_all')
              : action.selectedCount === 1
                ? i18n.t('ats.settings.templates.custom_questions.selection.delete.one')
                : i18n.t('ats.settings.templates.custom_questions.selection.delete.other', {
                    count: action.selectedCount,
                  }),
            icon: Delete,
            id: 'delete-all',
          },
        ],
      }
    },
    itemActions: (questionTemplate) => [
      {
        label: i18n.t('common.edit'),
        icon: Pencil,
        onClick: () => {
          setEditQuestionTemplate(questionTemplate)
        },
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => {
          removeQuestionTemplate(questionTemplate.id)
        },
      },
    ],
  })

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(action, selectedRows) => {
          if (action === 'delete-all') {
            const selectedIds = [...selectedRows.itemsStatus]
              .filter((v) => v.checked)
              .map((v) => v.item.id)
            openDialog({
              title: i18n.t('ats.settings.templates.custom_questions.delete.title'),
              illustration: 'delete',
              content: i18n.t('ats.settings.templates.custom_questions.delete.description'),
              cancelLabel: i18n.t('common.cancel'),
              confirmLabel: i18n.t('common.delete'),
              onConfirm: () => bulkRemoveQuestionTemplates(selectedIds),
            })
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('ats.settings.templates.custom_questions.table_headers.label'),
                  render: ({ label }) => label,
                },
                {
                  label: i18n.t(
                    'ats.settings.templates.custom_questions.table_headers.question_type'
                  ),
                  render: (item) => ({
                    type: 'dotTag',
                    value: {
                      color: getQuestionTagColor(item.questionType ?? 'default'),
                      label: getQuestionTypeLabel(item.questionType),
                    },
                  }),
                },
                {
                  label: i18n.t('ats.settings.templates.custom_questions.table_headers.mandatory'),
                  render: ({ mandatory }) => ({
                    type: 'status',
                    value: {
                      status: mandatory ? 'info' : 'neutral',
                      label: mandatory
                        ? capitalize(i18n.t('common.yes'))
                        : capitalize(i18n.t('common.no')),
                    },
                  }),
                },
                {
                  label: i18n.t(
                    'ats.settings.templates.custom_questions.table_headers.disqualifies'
                  ),
                  render: ({ questionType, autoDisqualify }) =>
                    questionType === 'multiple_choice' || questionType === 'single_choice'
                      ? {
                          type: 'status',
                          value: {
                            status: autoDisqualify ? 'info' : 'neutral',
                            label: autoDisqualify
                              ? capitalize(i18n.t('common.yes'))
                              : capitalize(i18n.t('common.no')),
                          },
                        }
                      : '-',
                },
              ],
            },
          },
        ]}
      />

      {editQuestionTemplate && (
        <EditQuestionTemplate
          questionTemplate={editQuestionTemplate}
          onClose={() => setEditQuestionTemplate(null)}
        />
      )}
    </>
  )
}
